// منصوبات (اشتراکات آب، برق و ...) یک ساختمان
import React, { useState, useEffect, useCallback } from 'react';
import ListPage, { ui } from '../../components/ListPage';
import { ExcelButton } from '../../components/excel';
import WarningDialog from '../../components/WarningDialog';
import { useFormButtons } from '../../utils/vbPerms';
import {
  API, Grid, Field, MessageBox, ChangeUsesDialog, SubFormShell, splitLines, visibleCols,
  FACILITIES_ITEMS, IDENTITY_TYPES,
} from './BuildingsShared';
import BuildingFacilitiesBill, { BILL_COLS } from './BuildingFacilitiesBill';

const FORM = 'FRMBUILDINGFACILITIES';
const BUTTONS = ['BTNNEW', 'BTNEDIT', 'BTNDELETE', 'BTNOK', 'BTNCANCEL', 'BTNBILL', 'BTNCHANGE', 'BTNMOVE'];

const COLS = [
  { key: 'facility', label: 'اشتراک' },
  { key: 'identity', label: 'شناسایی' },
  { key: 'facility_no', label: 'شماره' },
  { key: 'remark', label: 'توضیحات', wrap: true },
  { key: 'bill_count', label: 'تعداد قبض', num: true },
  { key: 'last_bill_date', label: 'تاریخ آخرین قبض' },
  { key: 'user_group', label: 'گروه کاربری' },
];

const EMPTY = { facility_id: 0, identity_type: 0, facility_no: '', remark: '' };

export default function BuildingFacilities({ user, onBack, building, year }) {
  const fb = useFormButtons(FORM, BUTTONS);
  const [rows, setRows] = useState([]);
  const [title, setTitle] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [bills, setBills] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [formErr, setFormErr] = useState('');
  const [msg, setMsg] = useState(null);
  const [ask, setAsk] = useState(null);
  const [uses, setUses] = useState(null);
  const [billOpen, setBillOpen] = useState(false);

  const load = useCallback(async (keepId) => {
    setLoading(true); setError('');
    const j = await API(`/${building.id}/facilities/?search=${encodeURIComponent(search)}`);
    setLoading(false);
    if (!j.ok) { setError(j.msg); return; }
    setRows(j.data);
    setTitle(j.building_title || '');
    const hit = keepId ? j.data.find(r => r.id === keepId) : null;
    setSelected(hit || j.data[0] || null);
  }, [building, search]);

  useEffect(() => { if (fb.loaded && fb.open) load(); }, [fb.loaded, fb.open]);

  useEffect(() => {
    if (!selected) { setBills([]); return; }
    API(`/facilities/${selected.id}/bills/`).then(j => setBills(j.ok ? j.data : []));
  }, [selected]);

  const showMsg = m => setMsg(splitLines(m));
  const F = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const has = !!selected;

  const openNew = () => { setForm(EMPTY); setFormErr(''); setModal('new'); };
  const openEdit = () => {
    if (!selected) return;
    setForm({ facility_id: selected.facility_id, identity_type: selected.identity_type,
      facility_no: selected.facility_no, remark: selected.remark });
    setFormErr(''); setModal('edit');
  };
  const save = async () => {
    const body = JSON.stringify({ ...form, building_id: building.id });
    const j = modal === 'new'
      ? await API('/facilities/create/', { method: 'POST', body })
      : await API(`/facilities/${selected.id}/update/`, { method: 'PUT', body });
    if (j.ok) { setModal(null); load(j.id); } else setFormErr(j.msg);
  };
  const doDelete = async () => {
    setAsk(null);
    const j = await API(`/facilities/${selected.id}/delete/`, { method: 'DELETE' });
    if (j.ok) load(); else showMsg(j.msg);
  };
  const askDelete = () => { if (selected) setAsk({ title: 'هشدار', lines: ['آيا از حذف اين رکورد مطمئن هستيد؟'], onYes: doDelete }); };

  const openUses = async () => {
    if (!selected) return;
    const j = await API(`/facilities/${selected.id}/uses/?year=${year}`);
    if (!j.ok || !j.cnt) { showMsg(j.msg); return; }
    setUses(j);
  };
  const doMove = async (targetId, thisYear) => {
    const j = await API(`/facilities/${selected.id}/move/`, { method: 'POST',
      body: JSON.stringify({ target_id: targetId, this_year: thisYear ? 1 : 0, year }) });
    if (!j.ok) { showMsg(j.msg); return; }
    setUses(null);
    setAsk({ title: 'انتخاب کنید', lines: ['تغییر انجام شد', '', '', 'آیا رکورد فعلی حذف شود؟'], onYes: () => { setAsk(null); askDelete(); } });
    load(selected.id);
  };

  const pageTitle = ` اشتراکات آب، برق و ...${title}`;
  const billTitle = selected
    ? `قبوض اشتراک ${selected.facility} ${title} با ${selected.identity} ${selected.facility_no}` : '';
  const shown = visibleCols(COLS, rows);

  const toolbar = (
    <>
      {fb.open && (
        <div style={ui.toolBtns}>
          {fb.can('BTNNEW') && <button onClick={openNew} style={{ ...ui.btnNew, cursor: 'pointer' }}>＋ جدید</button>}
          {fb.can('BTNEDIT') && rows.length > 0 && <button onClick={openEdit} disabled={!has} style={{ ...ui.btnEdit, cursor: 'pointer' }}>✎ ویرایش</button>}
          {fb.can('BTNDELETE') && rows.length > 0 && <button onClick={askDelete} disabled={!has} style={{ ...ui.btnDel, cursor: 'pointer' }}>✕ حذف</button>}
          {fb.can('BTNBILL') && <button onClick={() => selected && setBillOpen(true)} disabled={!has} style={{ ...ui.btnItems, cursor: 'pointer' }}>ثبت قبوض</button>}
          {fb.can('BTNCHANGE') && rows.length > 0 && <button onClick={openUses} disabled={!has} style={{ ...ui.btnItems, cursor: 'pointer' }}>تغییر موارد استفاده</button>}
        </div>
      )}
      <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && load()}
        style={{ ...ui.searchInput, flex: 1, minWidth: '160px' }} placeholder="جستجو" />
      <ExcelButton title={pageTitle} columns={shown} rows={rows} />
    </>
  );

  return (
    <>
      <ListPage user={user} onBack={onBack} title={pageTitle} subtitle="تدارکات — سامانه ارجان"
        toolbar={toolbar} error={error} loading={loading}>
        <div style={ui.splitWrap}>
          <div style={ui.topPane}>
            <Grid columns={shown} rows={rows} selectedId={selected && selected.id} onSelect={setSelected}
              loading={loading} empty="اشتراکی ثبت نشده است" />
          </div>
          <div style={ui.bottomPane}>
            {bills.length > 0 && <Grid columns={visibleCols(BILL_COLS, bills)} rows={bills} zebra="#fffde7" />}
          </div>
        </div>
      </ListPage>

      {modal && (
        <div style={ui.overlay} onClick={() => setModal(null)}>
          <div style={{ ...ui.modal, maxWidth: '600px' }} onClick={e => e.stopPropagation()}>
            <div style={ui.mHeader}>
              <div style={ui.mTitle}>{modal === 'new' ? 'اشتراک جدید' : 'ویرایش اشتراک'}</div>
              <button onClick={() => setModal(null)} style={ui.closeBtn}>✕</button>
            </div>
            <div style={ui.mBody}>
              {formErr && <div style={ui.errBox}>⚠️ {formErr}</div>}
              <div style={ui.row}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                  <label style={ui.mlabel}>اشتراک</label>
                  <select value={form.facility_id} onChange={e => F('facility_id', Number(e.target.value))} style={ui.input}>
                    {FACILITIES_ITEMS.map((t, i) => <option key={i} value={i}>{t}</option>)}
                  </select>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                  <label style={ui.mlabel}>نوع شناسایی</label>
                  <select value={form.identity_type} onChange={e => F('identity_type', Number(e.target.value))} style={ui.input}>
                    {IDENTITY_TYPES.map((t, i) => <option key={i} value={i}>{t}</option>)}
                  </select>
                </div>
                <Field label="شماره" value={form.facility_no} onChange={v => F('facility_no', v)} ltr />
              </div>
              <div style={ui.row}>
                <Field label="توضیحات" value={form.remark} onChange={v => F('remark', v)} />
              </div>
            </div>
            <div style={ui.mFooter}>
              {fb.can('BTNCANCEL') && <button onClick={() => setModal(null)} style={ui.btnCancel}>لغو</button>}
              {fb.can('BTNOK') && <button onClick={save} style={ui.btnSave}>ثبت</button>}
            </div>
          </div>
        </div>
      )}

      {uses && <ChangeUsesDialog info={uses} targetsEndpoint={`/facilities/${selected.id}/targets/`}
        notChosenMsg="منصوبات جایگزین انتخاب نشده است" onMove={doMove} onClose={() => setUses(null)} />}
      {ask && <WarningDialog title={ask.title} lines={ask.lines} confirmLabel="بلی" cancelLabel="خیر"
        onConfirm={ask.onYes} onCancel={() => setAsk(null)} tone="danger" />}
      {msg && <MessageBox lines={msg} onClose={() => setMsg(null)} />}

      {billOpen && selected && (
        <SubFormShell>
          <BuildingFacilitiesBill user={user} facility={selected} title={billTitle} year={year}
            onBack={() => { setBillOpen(false); load(selected.id); }} />
        </SubFormShell>
      )}
    </>
  );
}
